import React from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  UploadCloud,
  FileText,
  Trash2,
  EyeIcon,
  CircleX,
  AlertTriangle,
} from "lucide-react";

// Types
interface FileMetadata {
  name: string;
  url: string;
  blobName?: string;
  type?: string;
  size?: number;
}

interface FileUploadZoneProps {
  id: string;
  title: string;
  description: string;
  acceptedFormats: string;
  accept: string;
  multiple?: boolean;
  disabled?: boolean;
  onBrowseClick: () => void;
  disabledMessage?: string;
}

interface FileErrorMessageProps {
  message?: string;
}

interface AdminOnlyWarningProps {
  show: boolean;
}

interface SingleFileDisplayProps {
  file: File;
  fileName: string;
  fileSize: number;
  showDelete: boolean;
  onDelete: () => void;
}

interface ExistingFileDisplayProps {
  fileName: string;
  fileUrl?: string;
  isDraft?: boolean;
  showView: boolean;
  showDelete: boolean;
  onDelete: () => void;
}

interface RemovedFileMessageProps {
  show: boolean;
  message?: string;
}

interface MultipleFilesListProps {
  files: File[];
  title: string;
  showDelete: boolean;
  onDeleteFile: (index: number) => void;
}

interface ExistingFilesListProps {
  files: FileMetadata[];
  title: string;
  isDraft?: boolean;
  showView: boolean;
  showDelete: boolean;
  onDeleteFile: (blobName: string) => void;
}

// Compact version interfaces (no title required)
interface CompactExistingFilesListProps {
  files: FileMetadata[];
  isDraft?: boolean;
  showView: boolean;
  showDelete: boolean;
  onDeleteFile: (blobName: string) => void;
}

interface CompactMultipleFilesListProps {
  files: File[];
  showDelete: boolean;
  onDeleteFile: (index: number) => void;
}

// File Upload Zone Component
export const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  id,
  title,
  description,
  acceptedFormats,
  accept,
  multiple = false,
  disabled = false,
  onBrowseClick,
  disabledMessage,
}) => {
  return (
    <div className="border-2 border-dashed border-gray-200 rounded-lg p-6">
      <div className="flex flex-col items-center justify-center text-center gap-2">
        <UploadCloud className="h-10 w-10 text-muted-foreground" />
        <h3 className="text-md font-medium">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>

        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={onBrowseClick}
        >
          {multiple ? "Browse Files" : "Browse File"}
        </Button>

        {disabled && disabledMessage && (
          <p className="text-xs text-muted-foreground mt-1">{disabledMessage}</p>
        )}
      </div>
    </div>
  );
};

// File Error Message Component
export const FileErrorMessage: React.FC<FileErrorMessageProps> = ({ message }) => {
  if (!message) return null;
  
  return (
    <p className="text-sm text-red-600">
      {message}
    </p>
  );
};

// Admin Only Warning Component
export const AdminOnlyWarning: React.FC<AdminOnlyWarningProps> = ({ show }) => {
  if (!show) return null;

  return (
    <div className="flex items-center space-x-2 text-orange-600 bg-orange-50 border border-orange-200 rounded-lg p-3">
      <AlertTriangle className="h-4 w-4" />
      <span className="text-sm">Only admins can change files</span>
    </div>
  );
};

// Single File Display Component (for newly selected files)
export const SingleFileDisplay: React.FC<SingleFileDisplayProps> = ({
  file,
  fileName,
  fileSize,
  showDelete,
  onDelete,
}) => {
  return (
    <div className="flex items-center justify-between p-3 bg-green-50 rounded border border-green-200 mt-2">
      <div className="flex items-center space-x-2 flex-1">
        <FileText className="h-4 w-4 text-green-600 flex-shrink-0" />
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-sm font-medium truncate">{fileName}</span>
          <span className="text-xs text-muted-foreground">
            {fileSize.toFixed(2)} MB
          </span>
        </div>
      </div>
      {showDelete && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 flex-shrink-0"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

// Existing File Display Component (for files already on server)
export const ExistingFileDisplay: React.FC<ExistingFileDisplayProps> = ({
  fileName,
  fileUrl,
  isDraft = false,
  showView,
  showDelete,
  onDelete,
}) => {
  return (
    <div className={`flex items-center justify-between p-3 rounded border mt-2 ${
      isDraft ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200"
    }`}>
      <div className="flex items-center space-x-2 flex-1 min-w-0">
        <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
        <span className="text-sm truncate">{fileName}</span>
        {isDraft && (
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded flex-shrink-0">
            Draft
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {showView && fileUrl && (
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-blue-500 underline"
          >
            <EyeIcon className="h-4 w-4" /> View File
          </a>
        )}
        {showDelete && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-red-500 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Removed File Message Component
export const RemovedFileMessage: React.FC<RemovedFileMessageProps> = ({
  show,
  message = "File removed - will be deleted on save",
}) => {
  if (!show) return null;

  return (
    <div className="flex items-center mt-2 p-2 bg-red-50 border border-red-200 rounded-sm">
      <CircleX className="h-4 w-4 text-red-500 mr-2" />
      <span className="text-sm text-red-700">{message}</span>
    </div>
  );
};

// Multiple Files List Component (for newly selected multiple files)
export const MultipleFilesList: React.FC<MultipleFilesListProps> = ({
  files,
  title,
  showDelete,
  onDeleteFile,
}) => {
  if (!files || files.length === 0) return null;

  return (
    <div className="space-y-2 mt-2">
      <Label>{title} ({files.length})</Label>
      <div className="grid gap-2">
        {files.map((file, index) => (
          <div
            key={index}
            className="flex items-center space-x-2 p-2 bg-green-50 rounded border border-green-200"
          >
            <FileText className="h-4 w-4 text-green-600 flex-shrink-0" />
            <span className="text-sm flex-1 truncate">{file.name}</span>
            <span className="text-xs text-gray-500 flex-shrink-0">
              {(file.size / 1024 / 1024).toFixed(2)} MB
            </span>
            {showDelete && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onDeleteFile(index)}
                className="text-red-500 hover:text-red-700 flex-shrink-0"
              >
                <CircleX className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Existing Files List Component (for files already on server - multiple)
export const ExistingFilesList: React.FC<ExistingFilesListProps> = ({
  files,
  title,
  isDraft = false,
  showView,
  showDelete,
  onDeleteFile,
}) => {
  if (!files || files.length === 0) return null;

  return (
    <div className="space-y-2 mt-2">
      <Label>{title} ({files.length})</Label>
      <div className="grid gap-2">
        {files.map((file, index) => (
          <div
            key={index}
            className={`flex items-center space-x-2 p-2 rounded ${
              isDraft ? "bg-blue-50 border border-blue-200" : "bg-blue-50"
            }`}
          >
            <FileText className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <span className="text-sm flex-1 truncate">
              {file.name}
              {isDraft && (
                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                  Draft
                </span>
              )}
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              {showView && file.url && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open(file.url, "_blank")}
                >
                  <EyeIcon className="h-4 w-4" />
                </Button>
              )}
              {showDelete && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onDeleteFile(file.blobName || file.name)}
                >
                  <CircleX className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Compact version for inline display (used in sprinklerInfo, architecturalFile)
export const CompactExistingFilesList: React.FC<CompactExistingFilesListProps> = ({
  files,
  isDraft = false,
  showView,
  showDelete,
  onDeleteFile,
}) => {
  if (!files || files.length === 0) return null;

  return (
    <div className="grid gap-2 mt-2">
      {files.map((file, index) => (
        <div
          key={index}
          className={`flex items-center justify-between p-3 rounded border ${
            isDraft ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200"
          }`}
        >
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <span className="text-sm truncate">
              {file.name}
              {isDraft && (
                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                  Draft
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {showView && file.url && (
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-blue-500 underline"
              >
                <EyeIcon className="h-4 w-4" /> View File
              </a>
            )}
            {showDelete && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onDeleteFile(file.blobName || file.name)}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Compact version for newly selected files (inline display)
export const CompactMultipleFilesList: React.FC<CompactMultipleFilesListProps> = ({
  files,
  showDelete,
  onDeleteFile,
}) => {
  if (!files || files.length === 0) return null;

  return (
    <div className="grid gap-2 mt-2">
      {files.map((file, index) => (
        <div
          key={index}
          className="flex items-center justify-between p-3 bg-green-50 rounded border border-green-200"
        >
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <FileText className="h-4 w-4 text-green-600 flex-shrink-0" />
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-sm font-medium truncate">{file.name}</span>
              <span className="text-xs text-muted-foreground">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </span>
            </div>
          </div>
          {showDelete && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onDeleteFile(index)}
              className="text-red-500 hover:text-red-700 flex-shrink-0"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};
